import { useEffect, useRef, useState } from "react";
import { api } from "./session-api";
import { AuthSession, INITIAL_AUTH } from "./auth-session";
import { browserAuthStorage, platformAuthStorage } from "./auth-storage";
import { VkPlaySession } from "./vkplay-session";
import { VkGamesSession } from "./vkgames-session";
import { STANDALONE, type GamePlatform } from "./platform-launch";

export function useAuthSession(
  invalidate: () => void,
  platform: GamePlatform = STANDALONE,
) {
  const reset = useRef(invalidate);
  reset.current = invalidate;
  const [auth, setAuth] = useState(INITIAL_AUTH);
  const [binding] = useState(() => {
    const storage = browserAuthStorage();
    const gateway =
      platform.kind === "vkplay"
        ? new VkPlaySession(platform.appId, platform.launchParams, storage)
        : platform.kind === "vkgames"
          ? new VkGamesSession(platform.launchParams, storage)
        : null;
    return {
      gateway,
      storage:
        platform.kind !== "standalone"
          ? platformAuthStorage(storage, platform.appId, platform.kind)
          : storage,
    };
  });
  const [controller] = useState(
    () =>
      new AuthSession(
        binding.gateway?.authApi() ?? api,
        binding.storage,
        setAuth,
        () => reset.current(),
      ),
  );
  useEffect(() => {
    void controller.bootstrap();
    return () => controller.cancel();
  }, [controller]);
  return {
    auth,
    authController: controller,
    continuePlatformLogin: () =>
      binding.gateway?.continueLogin() ?? Promise.resolve(),
  };
}
